import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { DatePickerProps } from '@mui/x-date-pickers';
import type { FocusEventHandler, Ref } from 'react';
import type { InputBaseComponentProps } from '@mui/material';
import type { TextFieldProps } from '../TextField';
import { TextField } from '../TextField';
import { Box } from '../Box';

export interface DateFieldProps extends Omit<DatePickerProps<Date>, 'renderInput'> {
  /** When no input value, show placeholder */
  placeholder?: string;
  /** Helper text is displayed under the field */
  helperText?: string;
  /** Sets error state on field */
  error?: boolean;
  /** onBlur function passed to TextField */
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /** If true, the input will take up the full width of its container. */
  fullWidth?: boolean;
}

const DateTextField = (
  fieldProps: TextFieldProps,
  _inputProps?: InputBaseComponentProps,
  ref?: Ref<HTMLDivElement>,
) => {
  const { helperText, error, fullWidth, onBlur } = fieldProps;
  return (
    <Box ref={ref}>
      <TextField
        {...fieldProps}
        sx={{ button: { mr: 0, transform: 'scale(0.75)' } }}
        helperText={helperText}
        error={error}
        fullWidth={fullWidth}
        onBlur={onBlur}
      />
    </Box>
  );
};

/** Textfield component with date picker. Base on MUI X DatePicker: https://mui.com/x/react-date-pickers/date-picker/ */
export const DateField = ({
  placeholder,
  helperText,
  error,
  fullWidth,
  onBlur,
  ...props
}: DateFieldProps) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <DatePicker
      {...props}
      disableFuture
      slotProps={{
        textField: {
          placeholder,
          helperText,
          error,
          fullWidth,
          onBlur,
          sx: { button: { mr: 0, transform: 'scale(0.75)' } },
        },
      }}
      format="dd.MM.yyyy"
      slots={{ textField: DateTextField }}
    />
  </LocalizationProvider>
);

// eslint-disable-next-line import/no-default-export
export default DateField;
